import { useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { EDITOR } from "@/constants/testIds";

export default function MobileSectionCarousel({ sections, totalLabel, focusSection }) {
    const containerRef = useRef(null);
    const [active, setActive] = useState(0);

    // Track which slide is most visible via scroll
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        const onScroll = () => {
            const slides = Array.from(root.querySelectorAll("[data-slide]"));
            const center = root.scrollLeft + root.clientWidth / 2;
            let best = 0;
            let bestDist = Infinity;
            slides.forEach((s, i) => {
                const sCenter = s.offsetLeft + s.clientWidth / 2;
                const dist = Math.abs(sCenter - center);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = i;
                }
            });
            setActive(best);
        };
        root.addEventListener("scroll", onScroll, { passive: true });
        return () => root.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToIndex = (i) => {
        const root = containerRef.current;
        if (!root) return;
        const slide = root.querySelector(`[data-slide="${i}"]`);
        if (slide) {
            root.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        }
    };

    // External focus request (e.g. from validation)
    useEffect(() => {
        if (!focusSection?.id) return;
        const idx = sections.findIndex((s) => s.id === focusSection.id);
        if (idx >= 0) scrollToIndex(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusSection?.t]);

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                        {totalLabel ||
                            `Paso ${active + 1} de ${sections.length}`}
                    </div>
                    <h2 className="font-serif text-3xl leading-tight mt-1">
                        {sections[active]?.title}
                    </h2>
                    {sections[active]?.subtitle && (
                        <p className="text-sm text-[var(--ink-soft)] mt-1">
                            {sections[active].subtitle}
                        </p>
                    )}
                </div>
            </div>

            {/* Carousel */}
            <div
                ref={containerRef}
                data-testid={EDITOR.mobileCarousel}
                className="flex-1 overflow-x-auto overflow-y-hidden snap-x-mandatory no-scrollbar flex"
            >
                {sections.map((sec, i) => (
                    <div
                        key={sec.id}
                        data-slide={i}
                        className="snap-item shrink-0 w-screen h-full overflow-y-auto px-5 pb-32"
                    >
                        <div className="card-soft p-5 mt-2">{sec.render()}</div>
                    </div>
                ))}
            </div>

            {/* Footer controls */}
            <div className="px-5 pt-3 pb-20 flex items-center justify-between bg-[var(--bg)]/80 backdrop-blur-md border-t border-[var(--line)]">
                <button
                    data-testid={EDITOR.prevSection}
                    onClick={() => scrollToIndex(Math.max(0, active - 1))}
                    disabled={active === 0}
                    className="btn-ghost"
                    style={{ opacity: active === 0 ? 0.4 : 1 }}
                >
                    <ChevronLeft size={16} />
                </button>
                <div
                    data-testid={EDITOR.sectionDots}
                    className="dots"
                >
                    {sections.map((_, i) => (
                        <span
                            key={i}
                            className={`dot ${i === active ? "active" : ""}`}
                        />
                    ))}
                </div>
                <button
                    data-testid={EDITOR.nextSection}
                    onClick={() =>
                        scrollToIndex(
                            Math.min(sections.length - 1, active + 1),
                        )
                    }
                    disabled={active === sections.length - 1}
                    className="btn-primary"
                    style={{
                        opacity:
                            active === sections.length - 1 ? 0.4 : 1,
                        padding: "12px 16px",
                        fontSize: 13,
                    }}
                >
                    Siguiente
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
}
